/// <amd-module name="Build/CDPipelinesTab/Components/CDPipelinesTabService" />
import * as React from "react";
import { Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ObservableValue, ObservableArray, IReadonlyObservableArray } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { CDPipelinesTabView } from "Build/CDPipelinesTab/Components/CDPipelinesTabView";
import { CDPipelinesTabFilterBar } from "Build/CDPipelinesTab/Components/CDPipelinesTabFilterBar";
import { IRunViewContributedTabProps } from 'Build/Run/RunViewContributedTab';
import { IVssContributedTab } from "VSSUI/Tabs.Types";
import { IVssPageContext } from "VSS/Platform/Context";
import { CDPipelineRunDetails } from "Build/Client/Contracts/DataProvider";
import * as Resources from "Build/CDPipelinesTab/Resources";
import { ITabService } from "VSS/Features/PlatformUI/ContributedTab";
import * as Utils_String from "VSS/Core/Util/String";
import * as Build2_WebApi from "Build/Client/Contracts/Build";
import { Status } from "Build/Common/Library/Indicator";

class CDPipelinesTabService extends VssService implements ITabService {
    public getTabs(tabId: string, contextualData?: IRunViewContributedTabProps): IVssContributedTab[] | IReadonlyObservableArray<IVssContributedTab> {
        if (contextualData && contextualData.notifyUpdated) {
            this._contextualData = contextualData;
            this._updateCDPipelinesTab();
            this._contextualData.notifyUpdated.subscribe(this._updateCDPipelinesTab);
        }
        return this._tabs;
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        this._contextualData && this._contextualData.notifyUpdated && this._contextualData.notifyUpdated.unsubscribe(this._updateCDPipelinesTab);
        super._serviceEnd(pageContext);
    }

    private _renderCDPipelines = (): JSX.Element => {
        return (
            <Observer data={this._data}>
                {
                    (props: { data: CDPipelineRunDetails[] }) => (
                        <div>
                            {<CDPipelinesTabFilterBar updateRunDetailsData={this._updateRunDetailsData} />}
                            {<CDPipelinesTabView data={props.data} />}
                        </div>
                    )
                }
            </Observer>
        );
    }

    private _updateCDPipelinesTab = async (iterationCount = 1000): Promise<void> => {
        var syncData: CDPipelineRunDetails[] | undefined;

        this._getRunDetailsData().then(data => {
            if (data && data.length > 0) {
                data.forEach(data => {
                    this.runDetails.push(data);
                })
                this._continuationToken = data[data.length - 1].buildId;
                syncData = data;
                this._data.value = this.runDetails;
                if (syncData && syncData.length == this.paginationLimit && iterationCount > 1 ) {
                    return this._updateCDPipelinesTab(iterationCount-1);
                }               
            }
            if (this._tabs.length == 0 && this._data.value && this._data.value.length > 0) {
                this._tabs.push(this._tab);
            }
        });
    }

    private _getRunDetailsData = (): Promise<CDPipelineRunDetails[]> => {
        return new Promise((resolve, reject) => {
            const params: { [key: string]: string } = { "continuationToken": this._continuationToken + "", "buildId": this._contextualData.runId.value + "", "paginationLimit": this.paginationLimit + "" };
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync<CDPipelineRunDetails[]>(this._cdPipelinesDataProviderId, params, true).then(data => {
                if (data) {
                    resolve(data);
                }
                else {
                    reject(new Error("cant resolve"));
                }
            });
        });
    }

    private readonly _tab: IVssContributedTab = {
        id: "cdPipelines",
        name: Resources.CDPipelines,
        render: this._renderCDPipelines,
    };

    public _updateRunDetailsData = (status: string[]) => {
        var runData: CDPipelineRunDetails[] = [];
        if (status.length > 0) {
            this.runDetails.map(data => {
                var actualStatus: string = this._getStatusString(data.runStatus, data.runResult);
                status.forEach(status => {
                    if (Utils_String.equals(actualStatus, status, true)) {
                        runData.push(data);
                    }
                })
            });
        }
        else {
            runData = this.runDetails;
        }
        this._data.value = runData;
    }

    private _getStatusString = (runStatus: Build2_WebApi.BuildStatus, runResult: Build2_WebApi.BuildResult ): string => {
        switch(runStatus) {
            case Build2_WebApi.BuildStatus.NotStarted:
                return Status.Queued;
            case Build2_WebApi.BuildStatus.InProgress:
                return Status.InProgress;
            case Build2_WebApi.BuildStatus.Completed: {
                switch(runResult) {
                    case Build2_WebApi.BuildResult.Succeeded:
                        return Status.Succeeded;
                    case Build2_WebApi.BuildResult.PartiallySucceeded:
                        return Status.Warning;
                    case Build2_WebApi.BuildResult.Failed:
                        return Status.Failed;
                    case Build2_WebApi.BuildResult.Canceled:
                        return Status.Cancelled;
                }
            }    
            case Build2_WebApi.BuildStatus.Cancelling:
                return Status.Cancelled;
            default:
                return Status.Warning;            
        }
    }

    private _data: ObservableValue<CDPipelineRunDetails[] | undefined> = new ObservableValue(undefined);
    private _contextualData: IRunViewContributedTabProps;
    private _tabs = new ObservableArray<IVssContributedTab>();
    private readonly _cdPipelinesDataProviderId = "ms.vss-build-web.cd-pipelines-runs-details-for-ci-pipeline-run-data-provider";
    //Intializing _continuationToken to maximum Int32 value for fetching latest set of runs initially
    private _continuationToken: number = 2147483647;
    private paginationLimit: number = 100;
    private runDetails: CDPipelineRunDetails[] = [];
}

Services.add("cd-pipelines-tab-service", { serviceFactory: CDPipelinesTabService });
